export type Currency = "pln" | "eur";

export const CURRENCY_LABEL: Record<Currency, string> = {
  pln: "PLN",
  eur: "EUR",
};

export function getCurrencyForCountry(country: string): Currency {
  if (country === "PL") return "pln";
  return "eur";
}

// Product prices per currency (display values, not smallest unit)
export const PRODUCT_PRICES: Record<Currency, Record<string, number>> = {
  pln: { "Scale4G": 1999, "Scale": 1299, "nfc-tag": 10, "recorder": 349 },
  eur: { "Scale4G": 499, "Scale": 324.26, "nfc-tag": 2.50, "recorder": 0 },
};

// Use the page language, with a deterministic fallback. The machine's locale
// can differ between static rendering and the browser and break hydration.
export function formatPrice(amount: number, currency: Currency, locale = "en-GB"): string {
  return `${amount.toLocaleString(locale, {
    minimumFractionDigits: currency === "pln" ? 0 : 2,
    maximumFractionDigits: 2,
  })} ${CURRENCY_LABEL[currency]}`;
}


// Monthly prices always show two decimals ("2.50 PLN", "0.65 EUR")
export function formatMonthlyPrice(amount: number, currency: Currency, locale = "en-GB"): string {
  return `${amount.toLocaleString(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} ${CURRENCY_LABEL[currency]}`;
}

// Rounded for display: PLN whole ("10 PLN"), EUR one decimal ("2.5 EUR")
export function formatRoundedPrice(amount: number, currency: Currency, locale = "en-GB"): string {
  const digits = currency === "pln" ? 0 : 1;
  return `${amount.toLocaleString(locale, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })} ${CURRENCY_LABEL[currency]}`;
}

// Owner-account plan prices (gross, per year) in minor units. The backend
// catalog is authoritative for purchases; this map feeds static marketing copy.
export interface AccountPlanPrices {
  scale: number;
  basic: number;
  pro: number;
  extraScale: number;
  // One-time credit after buying the first scale (equals the Scale plan price).
  credit: number;
}

export const ACCOUNT_PLAN_PRICES: Record<Currency, AccountPlanPrices> = {
  pln: { scale: 9900, basic: 19900, pro: 44900, extraScale: 1900, credit: 9900 },
  eur: { scale: 2900, basic: 4900, pro: 11900, extraScale: 500, credit: 2900 },
};

// First-year price of a plan after the first-scale credit.
export const firstScalePromoMinor = (annualMinor: number, currency: Currency) =>
  Math.max(0, annualMinor - ACCOUNT_PLAN_PRICES[currency].credit);

// Subscription amounts: whole values without decimals ("29 EUR"), otherwise two ("4.50 EUR").
export function formatSubscriptionPrice(minor: number, currency: Currency, locale = "en-GB"): string {
  const amount = minor / 100;
  const digits = Number.isInteger(amount) ? 0 : 2;
  return `${amount.toLocaleString(locale, {
    minimumFractionDigits: digits,
    maximumFractionDigits: 2,
  })} ${CURRENCY_LABEL[currency]}`;
}

// Maps an API currency code ("PLN" / "EUR") to the site currency; unknown codes fall back to PLN.
export const currencyFromCode = (code: string | undefined): Currency =>
  code?.toLowerCase() === "eur" ? "eur" : "pln";

/**
 * Fills plan price tokens in translated copy: {price.scale}, {price.basic},
 * {price.pro}, {price.basicPromo}, {price.proPromo}, {price.extraScale},
 * {price.credit} and {price.currency}. Unknown tokens are left untouched.
 */
export function fillAccountPlanPrices(text: string, currency: Currency, locale = "en-GB"): string {
  if (!text.includes("{price.")) return text;
  const prices = ACCOUNT_PLAN_PRICES[currency];
  const values: Record<string, number> = {
    scale: prices.scale,
    basic: prices.basic,
    pro: prices.pro,
    basicPromo: firstScalePromoMinor(prices.basic, currency),
    proPromo: firstScalePromoMinor(prices.pro, currency),
    extraScale: prices.extraScale,
    credit: prices.credit,
  };
  return text.replace(/\{price\.(\w+)\}/g, (token, name: string) => {
    if (name === "currency") return CURRENCY_LABEL[currency];
    return name in values ? formatSubscriptionPrice(values[name], currency, locale) : token;
  });
}
